import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import OfficesDropdown from "../dropdowns/OfficesDropdown";
import Loading from "../loading/Loading";
import VoicemailNotificationLogsContainer from "./VoicemailNotificationLogsContainer";
import {
  fetchVoicemailStats,
  fetchVoicemailNotificationLogs,
} from "../../functions/office/voicemailStats";
import { formatDuration } from "../../functions/util/format";
import "./voicemail-stats-styling.css";

const STATUS_META = {
  pending: { label: "Pending", color: "#ffb703" },
  notified: { label: "Notified", color: "#fb8500" },
  resolved: { label: "Resolved", color: "#219ebc" },
  excluded: { label: "Excluded", color: "#808080" },
};

const LOGS_PER_PAGE = 15;

const VoicemailStatsContainer = () => {
  const [loading, setLoading] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [byStatus, setByStatus] = useState([]);
  const [topOffices, setTopOffices] = useState([]);

  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleOfficeChange = (officeId) => {
    setSelectedOffice(officeId);
  };

  const clearFilters = () => {
    setSelectedOffice(null);
    setFromDate("");
    setToDate("");
    setStatusFilter("");
  };

  const getStats = async () => {
    const res = await fetchVoicemailStats(selectedOffice, fromDate, toDate);
    if (res) {
      setByStatus(res.byStatus || []);
      setTopOffices(res.topOffices || []);
    } else {
      alert("Error: Failed to fetch voicemail notification stats.");
    }
  };

  const getLogs = async (page = 1) => {
    const res = await fetchVoicemailNotificationLogs({
      officeId: selectedOffice,
      status: statusFilter,
      from: fromDate,
      to: toDate,
      page,
      limit: LOGS_PER_PAGE,
    });

    if (res) {
      const { pagination } = res;
      setTotalPages(Math.ceil(pagination.total / pagination.limit));
      setLogs(page === 1 ? res.logs : (prev) => [...prev, ...res.logs]);
    } else {
      alert("Error: Failed to fetch voicemail notification logs.");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setCurrentPage(1);
      await Promise.all([getStats(), getLogs(1)]);
      setLoading(false);
    };
    load();
  }, [selectedOffice, fromDate, toDate, statusFilter]);

  useEffect(() => {
    if (currentPage !== 1) {
      const loadMore = async () => {
        setLoadingMore(true);
        await getLogs(currentPage);
        setLoadingMore(false);
      };
      loadMore();
    }
  }, [currentPage]);

  const chartData = Object.keys(STATUS_META).map((status) => {
    const entry = byStatus.find((s) => s._id === status);
    return {
      status: STATUS_META[status].label,
      count: entry ? entry.count : 0,
      fill: STATUS_META[status].color,
    };
  });

  return (
    <div className="comm-wrapper">
      <div className="comm-cards-wrapper">
        <div className="comm-half-container">
          <div className="comm-card-content-container">
            <p className="comm-title">Filter by Office</p>
            <div style={{ width: "80%" }}>
              <OfficesDropdown handleChange={handleOfficeChange} />
            </div>
          </div>
        </div>
        <div className="comm-half-container">
          <div className="comm-card-content-container">
            <p className="comm-title">Filter by Date</p>
            <div className="vm-stats-date-filters">
              <label>
                From
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </label>
            </div>
            <button className="comm-btn" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {loading === true ? <Loading /> : null}

      <div className="vm-stats-cards-wrapper">
        {Object.keys(STATUS_META).map((status) => {
          const entry = byStatus.find((s) => s._id === status);
          return (
            <div
              key={status}
              className="vm-stats-card"
              style={{ borderColor: STATUS_META[status].color }}
            >
              <p className="vm-stats-card-title">{STATUS_META[status].label}</p>
              <p className="vm-stats-card-count">{entry ? entry.count : 0}</p>
              <p className="vm-stats-card-sub">
                Avg to threshold: {formatDuration(entry?.avgMsToThreshold)}
              </p>
              <p className="vm-stats-card-sub">
                Avg to resolution: {formatDuration(entry?.avgMsToResolution)}
              </p>
            </div>
          );
        })}
      </div>

      <div className="vm-stats-chart-wrapper">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="status" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count">
              {chartData.map((entry) => (
                <Cell key={entry.status} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="vm-stats-top-offices-container">
        <p className="comm-title">Top Overdue Offices</p>
        {topOffices.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th className="table-heading">Office</th>
                  <th className="table-heading">Office Sys Name</th>
                  <th className="table-heading">Overdue Count</th>
                </tr>
              </thead>
              <tbody>
                {topOffices.map((office) => (
                  <tr key={office.officeId}>
                    <td className="table-item">{office.officeName}</td>
                    <td className="table-item">{office.officeSysName}</td>
                    <td className="table-item">{office.overdueCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No overdue offices to display.</p>
        )}
      </div>

      <VoicemailNotificationLogsContainer
        logs={logs}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      {currentPage !== totalPages && !loading && logs.length > 0 && (
        <div className="flex w-full justify-center py-5">
          <button
            className="px-5 py-3 bg-blue-600 text-white font-semibold text-sm rounded hover:shadow-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:shadow-none"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages || loadingMore}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

export default VoicemailStatsContainer;
